/* Where the API's GitHub Setup URL hands the browser back.

   GitHub sends people to the Setup URL after any change to the App's installation --
   including changes made on GitHub, which carry none of our state. The API cannot tell
   who is in that browser, so it writes only what its state token proves and passes the
   rest here. This page is signed in, so it can ask `resume` what the installation means
   for this person: already theirs, theirs in another workspace, or not yet linked.

   Nothing here moves an installation between workspaces on its own. "Move it here" is a
   disconnect the person confirms, followed by the ordinary install flow. */

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { api } from "@/api";
import { AccessPage, QuietLink } from "@/pages/access";
import { useSession } from "@/session";
import { Button } from "@/ui";

/* Outcomes the Setup URL settled itself, with nothing left to ask the API. */
const SETTLED = {
  requested: {
    title: "Waiting for approval",
    detail:
      "Your request was sent to the GitHub organization's owners. Once one of them approves it, connect GitHub again from Settings.",
  },
  not_found: {
    title: "Installation not found",
    detail: "GitHub no longer has that installation. Connect GitHub again from Settings.",
  },
  missing: {
    title: "Nothing to connect",
    detail: "GitHub did not say which installation changed. Connect GitHub again from Settings.",
  },
};

const text = { font: "var(--text-body-14)", color: "var(--text-muted)", margin: 0, textAlign: "center" };
const stack = { display: "grid", gap: "14px", justifyItems: "center" };

export function GithubReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { orgId, refresh, selectOrganization } = useSession();
  const [view, setView] = useState({ kind: "working" });
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  const status = params.get("status");
  const installationId = /^\d{1,18}$/.test(params.get("installation_id") ?? "")
    ? params.get("installation_id")
    : null;

  /* Into the workspace that holds the installation: the dashboard once set up, otherwise
     back into the wizard, which reads `?org=` as "returned from GitHub". */
  async function land(id) {
    selectOrganization(id);
    const session = await refresh();
    navigate(session?.profile?.onboardingCompleted ? "/" : `/onboarding?org=${encodeURIComponent(id)}`, {
      replace: true,
    });
  }

  async function resume() {
    setView({ kind: "working" });
    try {
      const result = await api.post(`/github/installations/${installationId}/resume`, { orgId });
      if (result.status === "linked") {
        await land(result.orgId);
        return;
      }
      setView({ kind: result.status, ...result });
    } catch (failure) {
      setView({ kind: "failed", message: failure.message });
    }
  }

  /* A fresh trip through `/install`, whose state token links the installation to the
     workspace open now. */
  async function connectHere() {
    if (!orgId) {
      navigate("/", { replace: true });
      return;
    }
    setBusy(true);
    try {
      const { url } = await api.get(`/organizations/${orgId}/github/install-url`);
      window.location.assign(url);
    } catch (failure) {
      setBusy(false);
      setView({ kind: "failed", message: failure.message });
    }
  }

  async function moveHere(fromOrgId) {
    setBusy(true);
    try {
      await api.del(`/organizations/${fromOrgId}/github/installations/${installationId}`);
    } catch (failure) {
      setBusy(false);
      setView({ kind: "failed", message: failure.message });
      return;
    }
    await connectHere();
  }

  useEffect(() => {
    /* Once. In development the effect runs twice on mount. */
    if (started.current) return;
    started.current = true;
    if (status === "linked" && params.get("org")) {
      land(params.get("org"));
    } else if (SETTLED[status]) {
      setView({ kind: "settled", ...SETTLED[status] });
    } else if (installationId) {
      resume();
    } else {
      setView({ kind: "settled", ...SETTLED.missing });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const home = <QuietLink onClick={() => navigate("/", { replace: true })}>Back to CodeNerve</QuietLink>;

  if (view.kind === "linked_elsewhere") {
    if (view.confirming) {
      return (
        <AccessPage title="Move GitHub here?" lead={`This disconnects GitHub from ${view.orgName}.`}>
          <div style={stack}>
            <p style={text}>
              Reviews stop for the repositories in {view.orgName}. You then confirm the connection on GitHub once
              more to link it to this workspace.
            </p>
            <Button disabled={busy} onClick={() => moveHere(view.orgId)}>
              Disconnect and continue
            </Button>
            <QuietLink onClick={() => setView({ ...view, confirming: false })}>Cancel</QuietLink>
          </div>
        </AccessPage>
      );
    }
    return (
      <AccessPage title="GitHub is already connected" lead={`It is connected to your ${view.orgName} workspace.`}>
        <div style={stack}>
          <Button disabled={busy} onClick={() => land(view.orgId)}>
            Open {view.orgName}
          </Button>
          {view.canDisconnect && orgId && (
            <Button tone="secondary" disabled={busy} onClick={() => setView({ ...view, confirming: true })}>
              Move it to this workspace
            </Button>
          )}
          {home}
        </div>
      </AccessPage>
    );
  }

  if (view.kind === "reconnect") {
    return (
      <AccessPage title="Finish connecting GitHub" lead="Confirm the connection on GitHub once more.">
        <div style={stack}>
          <p style={text}>GitHub will show your repository access again. Click Save there to link it to this workspace.</p>
          <Button disabled={busy} onClick={connectHere}>
            Continue to GitHub
          </Button>
          {home}
        </div>
      </AccessPage>
    );
  }

  if (view.kind === "failed") {
    return (
      <AccessPage title="GitHub was not connected" lead={view.message}>
        <div style={stack}>
          {installationId && (
            <Button tone="secondary" onClick={resume}>
              Try again
            </Button>
          )}
          {home}
        </div>
      </AccessPage>
    );
  }

  if (view.kind === "settled") {
    return (
      <AccessPage title={view.title} lead={view.detail}>
        <div style={stack}>
          <QuietLink onClick={() => navigate("/settings/providers", { replace: true })}>Go to Settings</QuietLink>
          {home}
        </div>
      </AccessPage>
    );
  }

  return (
    <AccessPage title="GitHub" lead="Finishing your GitHub connection…">
      <p style={text}>One moment.</p>
    </AccessPage>
  );
}
